import React from "react";
import { motion } from "framer-motion";
import { PackageCheck, ChefHat, Truck, Home, XCircle, Clock } from "lucide-react";
import { useBrand } from "../context/BrandContext";

export const ORDER_STEPS = [
  { key: "recue", label: "Reçue", icon: PackageCheck },
  { key: "preparation", label: "En préparation", icon: ChefHat },
  { key: "livraison", label: "En livraison", icon: Truck },
  { key: "livree", label: "Livrée", icon: Home },
];

export const statusToStep = (status) => {
  switch (status) {
    case "nouvelle":
    case "confirmee":
      return 0;
    case "en_preparation":
    case "planifiee":
      return 1;
    case "en_livraison":
      return 2;
    case "livree":
      return 3;
    default:
      return 0;
  }
};

export const OrderProgress = ({ status, compact = false }) => {
  const brand = useBrand();
  if (status === "annulee") {
    return (
      <div className="flex items-center gap-2 text-destructive" data-testid="progress-cancelled">
        <XCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Commande annulée</span>
      </div>
    );
  }

  if (status === "refusee") {
    return (
      <div className="flex items-center gap-2 text-destructive" data-testid="progress-refused">
        <XCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Commande refusée</span>
      </div>
    );
  }

  if (status === "en_attente") {
    return (
      <div className="flex items-center gap-2 text-amber-600" data-testid="progress-pending">
        <Clock className="h-4 w-4" />
        <span className="text-sm font-medium">En attente de validation par {brand.name}</span>
      </div>
    );
  }

  const current = statusToStep(status);
  const dot = compact ? "h-8 w-8" : "h-11 w-11";
  const ico = compact ? "h-4 w-4" : "h-5 w-5";
  const lineMt = compact ? "mt-4" : "mt-[22px]";

  return (
    <div className="w-full" data-testid="order-progress">
      <div className="flex items-start">
        {ORDER_STEPS.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const isTruck = s.key === "livraison";
          const Icon = s.icon;
          const activeStyle = active && isTruck
            ? "bg-secondary text-secondary-foreground ring-secondary"
            : "bg-primary text-primary-foreground ring-primary";
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-1.5" data-testid={`step-${s.key}${active ? "-active" : done ? "-done" : ""}`}>
                <motion.div
                  className={`flex items-center justify-center rounded-full ring-2 transition-colors ${dot} ${
                    done ? "bg-primary text-primary-foreground ring-primary" : active ? activeStyle : "bg-muted text-muted-foreground ring-border"
                  }`}
                  animate={active ? { scale: [1, 1.09, 1] } : { scale: 1 }}
                  transition={active ? { repeat: Infinity, duration: 1.6, ease: "easeInOut" } : { duration: 0.2 }}
                >
                  {isTruck && active ? (
                    <motion.div animate={{ x: [-2, 2, -2] }} transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}>
                      <Icon className={ico} />
                    </motion.div>
                  ) : (
                    <Icon className={ico} />
                  )}
                </motion.div>
                {!compact && (
                  <span className={`whitespace-nowrap text-xs font-medium ${active ? (isTruck ? "text-secondary" : "text-primary") : done ? "text-foreground" : "text-muted-foreground"}`}>
                    {s.label}
                  </span>
                )}
              </div>
              {i < ORDER_STEPS.length - 1 && (
                <div className={`h-1 flex-1 rounded-full ${lineMt} ${compact ? "mx-1" : "mx-2"} ${i < current ? "bg-primary" : "bg-muted"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
